// One call from plot and rules to ranked, checked options with floor plans.
import { envelope, type Envelope } from "./envelope.ts";
import { area, union, type MultiPoly } from "./geom.ts";
import type { Unit } from "./layout.ts";
import { indicators, limits, type Indicators } from "./indicators.ts";
import { sitePlan, type SitePlan } from "./siteplan.ts";
import { layout, type Layout } from "./layout.ts";
import { options, type Option } from "./massing.ts";
import { compliance, dwellingsFromLayout, parking, type Check, type Dwellings, type Parking } from "./programme.ts";
import type { Programme, Rules, Site } from "./rules.ts";

export interface Generated {
  option: Option;
  /** What stops the building growing, in the words a developer uses. */
  binding: string[];
  /** Flats with windows on two or more orientations, as a share. */
  dualAspect: number;
  /** How far the flats built are from the mix asked for: 0 exact, 1 nothing alike. */
  mixDistance: number;
  /** Accesses, footpaths, green space and the territorial balance. */
  site: SitePlan;
  /** POT and CUT as the law computes them: balconies, what stays on the plot. */
  indicators: Indicators;
  layout: Layout;
  dwellings: Dwellings;
  parking: Parking;
  checks: Check[];
}

export interface Result {
  envelope: Envelope;
  options: Generated[];
}

/**
 * Twice as many candidates as will be shown are laid out, and the shortlist
 * is ranked by the usable area of flats whose living room gets its 1½ hours
 * of winter sun, not by gross area: a courtyard too narrow for its height,
 * or a corridor wing with half its flats facing north, builds many square
 * metres that an architect would not propose.
 */
function binding(o: Option, rules: Rules, ind: Indicators, prog: Programme): string[] {
  const lim = limits(rules, 1);
  const out: string[] = [];
  if (o.limitedBy === "cut" || ind.cut >= lim.cutLimit * 0.98) out.push("CUT");
  if (ind.pot >= lim.potLimit * 0.97) out.push("POT");
  if (rules.maxStoreys !== undefined && o.storeys.length >= rules.maxStoreys) out.push("regimul de înălțime");
  else if (o.height + prog.storeyHeight > rules.maxHeight) out.push("înălțimea maximă");
  if (!out.length) out.push("retragerile");
  return out;
}

function dualAspect(l: Layout): number {
  const flats = l.floors.flatMap((f) => f.units).filter((u) => u.kind === "apartment");
  const two = flats.filter((u) => (u.windows ?? []).some((a, i, w) => w.slice(i + 1).some((b) => a.normal[0] * b.normal[0] + a.normal[1] * b.normal[1] < 0.7)));
  return flats.length ? two.length / flats.length : 0;
}

function mixDistance(d: Dwellings, prog: Programme): number {
  const total = d.total || 1;
  const want = prog.mix.reduce((t, m) => t + m.share, 0) || 1;
  const types = new Set([...prog.mix.map((m) => m.type), ...d.byType.map((b) => b.type)]);
  let sum = 0;
  for (const t of types) sum += Math.abs((d.byType.find((b) => b.type === t)?.count ?? 0) / total - (prog.mix.find((m) => m.type === t)?.share ?? 0) / want);
  return sum / 2;
}

export function generate(site: Site, rules: Rules, prog: Programme, limit = 5): Result {
  const env = envelope(site, rules, prog);
  const all = options(env, rules, prog, limit * 2).map((option) => {
      const l = layout(env, option, prog, site.latitude, site.context);
      const d = dwellingsFromLayout(option, prog, l);
      const cores = union(...l.floors[0].units.filter((u) => u.kind === "core").map((u) => u.polygon));
      const p = parking(env, rules, prog, d, site, cores, l, option);
      let balconies = l.floors.flatMap((f) => f.units.map((u) => u.balcony).filter((b): b is NonNullable<typeof b> => !!b));
      let ind = indicators(rules, env.plotArea, option.storeys.map((s) => s.footprint), balconies, option.grossArea);
      // Massing reserved room for balconies by estimate; the drawn ones are
      // what POT counts. If they overshoot, whole stacks come off, the largest
      // first, until it fits: a balcony may be wished for, POT is the law.
      const lim = limits(rules, env.plotArea);
      let removed = 0;
      if (ind.pot > lim.potLimit + 1e-9) {
        const key = (b: MultiPoly) => { const q = b[0][0]; return `${Math.round(q.reduce((t, p) => t + p[0], 0) / q.length)},${Math.round(q.reduce((t, p) => t + p[1], 0) / q.length)}`; };
        const stacks = new Map<string, { area: number; units: Unit[] }>();
        for (const u of l.floors.flatMap((f) => f.units)) if (u.balcony) {
          const k = key(u.balcony);
          const st = stacks.get(k) ?? { area: 0, units: [] };
          st.area = Math.max(st.area, area(u.balcony)); st.units.push(u);
          stacks.set(k, st);
        }
        for (const st of [...stacks.values()].sort((a, b) => b.area - a.area)) {
          for (const u of st.units) { u.balcony = undefined; u.openings = u.openings?.map((op) => (op.kind === "balconyDoor" ? { ...op, kind: "window" as const } : op)); }
          removed++;
          balconies = l.floors.flatMap((f) => f.units.map((u) => u.balcony).filter((b): b is NonNullable<typeof b> => !!b));
          ind = indicators(rules, env.plotArea, option.storeys.map((s) => s.footprint), balconies, option.grossArea);
          if (ind.pot <= lim.potLimit + 1e-9) break;
        }
      }
      l.balconiesRemoved = removed;
      const sp = sitePlan(env, site, option, l, p);
      return { option, binding: binding(option, rules, ind, prog), dualAspect: dualAspect(l), mixDistance: mixDistance(d, prog), site: sp, indicators: ind, layout: l, dwellings: d, parking: p, checks: compliance(env, rules, option, d, p, ind, l, sp, site) };
    });
  all.sort((a, b) => b.layout.sunlitUsable - a.layout.sunlitUsable || b.option.grossArea - a.option.grossArea);
  return { envelope: env, options: all.slice(0, limit) };
}
